import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users, ShieldCheck, ShieldAlert, Trash2, Undo2, Ban, Search, UserCheck, Crown,
  X, Pencil, Save, Activity, ScrollText, KeyRound, AlertTriangle, Eye,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AdminDevTeams from "@/components/admin/AdminDevTeams";

const EASE = [0.16, 1, 0.3, 1];
const ROLE_ORDER = { admin: 0, dev: 1, citizen: 2 };
const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2 font-mono text-sm text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";
const labelCls = "font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";

const ACTION_META = {
  "auth.login_mfa": { label: "Portal login (2FA)", cls: "text-nc-green" },
  "auth.login_failed": { label: "FAILED login attempt", cls: "text-red-400" },
  "auth.mfa_failed": { label: "FAILED 2FA code", cls: "text-red-400" },
  "auth.2fa_enabled": { label: "2FA enrolled", cls: "text-nc-blue" },
  "account.role_change": { label: "Role changed", cls: "text-nc-orange" },
  "account.ban": { label: "Account BANNED", cls: "text-red-400" },
  "account.unban": { label: "Account unbanned", cls: "text-nc-green" },
  "account.edit": { label: "Account edited", cls: "text-zinc-300" },
  "account.delete": { label: "Account DELETED", cls: "text-red-400" },
  "account.reset_2fa": { label: "2FA reset", cls: "text-nc-orange" },
  "docs.delete": { label: "Document deleted", cls: "text-nc-orange" },
};

function roleBadge(role) {
  return role === "admin"
    ? "text-nc-orange border-nc-orange/40 bg-nc-orange/10"
    : role === "dev"
      ? "text-nc-blue border-nc-blue/40 bg-nc-blue/10"
      : "text-zinc-400 border-white/15 bg-white/5";
}

function AuditRow({ e, i, prefix }) {
  const meta = ACTION_META[e.action] || { label: e.action, cls: "text-zinc-400" };
  return (
    <div data-testid={`${prefix}-event-${i + 1}`} className="flex items-start gap-3 border-b border-white/5 py-2.5 font-mono text-[11px]">
      <span className="text-zinc-600 shrink-0 w-[130px]">{String(e.at).slice(5, 19).replace("T", " ")}</span>
      <span className={`shrink-0 w-[170px] uppercase tracking-wide ${meta.cls}`}>{meta.label}</span>
      <span className="text-zinc-300 shrink-0 truncate max-w-[180px]">{e.actor_email || "—"}</span>
      <span className="text-zinc-500 truncate">{e.detail || e.target}</span>
    </div>
  );
}

function AccountDrawer({ account, me, onClose, onAction }) {
  const [editName, setEditName] = useState(account.name || "");
  const [editBio, setEditBio] = useState(account.bio || "");
  const [activity, setActivity] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/audit/user/${account.id}`).then((r) => setActivity(r.data)).catch(() => {});
  }, [account.id]);

  const act = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      if (ok) toast.success(ok);
      onAction();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const isSelf = me?.id === account.id;

  return (
    <motion.div
      data-testid="account-drawer"
      className="fixed inset-y-0 right-0 z-[90] w-full max-w-lg bg-[#070707] border-l border-nc-blue/30 flex flex-col shadow-[0_0_60px_rgba(46,107,255,0.15)]"
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "100%" }}
      transition={{ duration: 0.35, ease: EASE }}
    >
      <div className="border-b border-white/10 px-5 py-4 flex items-center justify-between">
        <div>
          <p className="font-mono text-[9px] tracking-[0.3em] text-nc-orange uppercase">Personnel File // {account.role.toUpperCase()}</p>
          <h3 data-testid="drawer-user-name" className="font-sans font-bold uppercase text-lg text-zinc-50 mt-1">{account.name || "—"}</h3>
        </div>
        <button data-testid="drawer-close" onClick={onClose} className="text-zinc-500 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-6">
        {/* Identity */}
        <div className="flex items-center gap-4">
          {account.avatar_path ? (
            <img src={`${process.env.REACT_APP_BACKEND_URL}/api/profile/avatar/${account.id}`} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-nc-blue/40" />
          ) : (
            <span className="w-16 h-16 rounded-full border-2 border-white/15 bg-nc-blue/10 flex items-center justify-center font-sans font-bold text-xl text-nc-blue">
              {(account.name || account.email).slice(0, 1).toUpperCase()}
            </span>
          )}
          <div className="min-w-0">
            <p className="font-mono text-xs text-zinc-300 truncate">{account.email}</p>
            <div className="flex gap-2 mt-2 flex-wrap">
              <span className={`font-mono text-[8px] tracking-[0.2em] uppercase border px-1.5 py-0.5 ${roleBadge(account.role)}`}>{account.role}</span>
              {account.banned && <span className="font-mono text-[8px] tracking-[0.2em] uppercase border border-red-500/50 bg-red-500/10 text-red-400 px-1.5 py-0.5">banned</span>}
              {account.totp_enabled && <span className="font-mono text-[8px] tracking-[0.2em] uppercase border border-nc-green/40 bg-nc-green/10 text-nc-green px-1.5 py-0.5">2FA on</span>}
              {account.auth_provider === "google" && <span className="font-mono text-[8px] tracking-[0.2em] uppercase border border-white/20 text-zinc-400 px-1.5 py-0.5">google</span>}
            </div>
            {account.created_at && <p className="font-mono text-[9px] text-zinc-600 mt-2">Registered {String(account.created_at).slice(0, 10)}</p>}
          </div>
        </div>

        {account.banned && account.ban_reason && (
          <div className="border border-red-500/30 bg-red-500/5 p-4">
            <p className="font-mono text-[9px] tracking-[0.25em] text-red-400 uppercase mb-1">Active ban reason</p>
            <p className="font-mono text-xs text-zinc-300 leading-relaxed">{account.ban_reason}</p>
          </div>
        )}

        {/* Edit */}
        <div className="border border-white/10 p-4 flex flex-col gap-3">
          <p className="font-mono text-[9px] tracking-[0.25em] text-nc-blue uppercase flex items-center gap-2"><Pencil className="w-3 h-3" /> Edit Information</p>
          <div>
            <label className={labelCls}>Display name</label>
            <input data-testid="drawer-name-input" className={inputCls} value={editName} onChange={(e) => setEditName(e.target.value)} minLength={2} maxLength={24} />
          </div>
          <div>
            <label className={labelCls}>Bio</label>
            <textarea data-testid="drawer-bio-input" rows={2} maxLength={500} className={`${inputCls} resize-none`} value={editBio} onChange={(e) => setEditBio(e.target.value)} />
          </div>
          <button data-testid="drawer-save-button" disabled={busy || editName.trim().length < 2} onClick={() => act(() => api.patch(`/users/${account.id}`, { name: editName, bio: editBio }), "Account updated")} className="self-start bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-4 py-2 flex items-center gap-2 transition-colors">
            <Save className="w-3.5 h-3.5" /> Save changes
          </button>
        </div>

        {/* Role & access */}
        {!isSelf && (
          <div className="border border-white/10 p-4 flex flex-col gap-3">
            <p className="font-mono text-[9px] tracking-[0.25em] text-nc-orange uppercase flex items-center gap-2"><ShieldCheck className="w-3 h-3" /> Clearance & Access</p>
            <div className="flex gap-2 flex-wrap">
              {account.role !== "admin" && (
                <button data-testid="drawer-grant-admin" disabled={busy} onClick={() => act(() => api.patch(`/users/${account.id}/role`, { role: "admin" }), "Promoted to admin")} className="font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-2 border border-nc-orange/40 text-nc-orange hover:bg-nc-orange/10 transition-colors flex items-center gap-1.5">
                  <Crown className="w-3 h-3" /> Make Admin
                </button>
              )}
              {account.role !== "dev" ? (
                <button data-testid="drawer-grant-dev" disabled={busy} onClick={() => act(() => api.patch(`/users/${account.id}/role`, { role: "dev" }), "Granted dev role")} className="font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-2 border border-nc-blue/40 text-nc-blue hover:bg-nc-blue/10 transition-colors flex items-center gap-1.5">
                  <ShieldCheck className="w-3 h-3" /> Make Dev
                </button>
              ) : (
                <button data-testid="drawer-revoke-dev" disabled={busy} onClick={() => act(() => api.patch(`/users/${account.id}/role`, { role: "citizen" }), "Demoted to citizen")} className="font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-2 border border-white/20 text-zinc-400 hover:border-white/40 transition-colors flex items-center gap-1.5">
                  <UserCheck className="w-3 h-3" /> Demote
                </button>
              )}
              {account.totp_enabled && (
                <button data-testid="drawer-reset-2fa" disabled={busy} onClick={() => act(() => api.post(`/users/${account.id}/reset-2fa`), "2FA wiped — user must re-enroll")} className="font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-2 border border-nc-orange/40 text-nc-orange hover:bg-nc-orange/10 transition-colors flex items-center gap-1.5">
                  <KeyRound className="w-3 h-3" /> Reset 2FA
                </button>
              )}
              <button data-testid="drawer-toggle-team" disabled={busy} onClick={() => act(() => api.patch(`/users/${account.id}/team`, { on_team: !account.studio_team }), account.studio_team ? "Removed from the public team page" : "Now showing on the public team page")}
                className={`font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-2 border transition-colors flex items-center gap-1.5 ${account.studio_team ? "border-nc-green/40 text-nc-green hover:bg-nc-green/10" : "border-white/20 text-zinc-400 hover:border-white/40"}`}>
                <Users className="w-3 h-3" /> {account.studio_team ? "On Team Page — Remove" : "Show on Team Page"}
              </button>
            </div>
            <div className="flex gap-2 flex-wrap border-t border-white/10 pt-3">
              {account.banned ? (
                <button data-testid="drawer-unban" disabled={busy} onClick={() => act(() => api.post(`/users/${account.id}/unban`), "Account unbanned")} className="font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-2 border border-nc-green/40 text-nc-green hover:bg-nc-green/10 transition-colors flex items-center gap-1.5">
                  <Undo2 className="w-3 h-3" /> Unban
                </button>
              ) : (
                <button data-testid="drawer-ban" disabled={busy} onClick={() => act(() => api.post(`/users/${account.id}/ban`, { reason: "Suspended by studio administration — contact support to appeal." }), "Account banned")} className="font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-2 border border-red-400/40 text-red-400 hover:bg-red-400/10 transition-colors flex items-center gap-1.5">
                  <Ban className="w-3 h-3" /> Ban Account
                </button>
              )}
              {!confirmDelete ? (
                <button data-testid="drawer-delete" disabled={busy} onClick={() => setConfirmDelete(true)} className="font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-2 border border-red-500/50 text-red-400 hover:bg-red-500/10 transition-colors flex items-center gap-1.5">
                  <Trash2 className="w-3 h-3" /> Delete Account
                </button>
              ) : (
                <div className="flex items-center gap-2 border border-red-500/50 bg-red-500/10 px-3 py-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                  <span className="font-mono text-[9px] text-red-300 uppercase">Permanent — sure?</span>
                  <button data-testid="drawer-delete-confirm" disabled={busy} onClick={() => act(() => api.delete(`/users/${account.id}`), "Account permanently deleted")} className="font-mono text-[9px] uppercase px-2 py-1 bg-red-500 hover:bg-red-600 text-white transition-colors">Yes, delete</button>
                  <button data-testid="drawer-delete-cancel" onClick={() => setConfirmDelete(false)} className="font-mono text-[9px] uppercase px-2 py-1 text-zinc-400">No</button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Activity */}
        <div className="border border-white/10 p-4">
          <p className="font-mono text-[9px] tracking-[0.25em] text-nc-blue uppercase flex items-center gap-2 mb-3"><Activity className="w-3 h-3" /> Account Activity ({activity.length})</p>
          {activity.length === 0 && <p className="font-mono text-[10px] text-zinc-600">No recorded activity for this account.</p>}
          {activity.slice(0, 12).map((e, i) => <AuditRow key={e.id} e={e} i={i} prefix="drawer" />)}
        </div>
      </div>
    </motion.div>
  );
}

export default function AdminTeam() {
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [removals, setRemovals] = useState([]);
  const [audit, setAudit] = useState([]);
  const [auditStats, setAuditStats] = useState(null);
  const [auditFilter, setAuditFilter] = useState("");
  const [search, setSearch] = useState("");
  const [me, setMe] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/dev/removal-requests").then((r) => setRemovals(r.data)).catch(() => {});
    api.get("/audit").then((r) => setAudit(r.data)).catch(() => {});
    api.get("/audit/stats").then((r) => setAuditStats(r.data)).catch(() => {});
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, okMsg) => {
    try {
      await fn();
      toast.success(okMsg);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const approveRemoval = (docId) => act(() => api.delete(`/dev/docs/${docId}`), "Document permanently deleted");
  const rejectRemoval = (docId) => act(() => api.post(`/dev/docs/${docId}/removal-cancel`), "Removal rejected — doc kept");

  const filtered = users
    .filter((u) => !search || u.email.toLowerCase().includes(search.toLowerCase()) || (u.name || "").toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => (ROLE_ORDER[a.role] ?? 3) - (ROLE_ORDER[b.role] ?? 3) || a.email.localeCompare(b.email));

  const filteredAudit = audit.filter((e) => !auditFilter || e.action.includes(auditFilter) || (e.actor_email || "").toLowerCase().includes(auditFilter.toLowerCase()));

  const stats = [
    { label: "Accounts", value: users.length, icon: Users },
    { label: "Admins", value: users.filter((u) => u.role === "admin").length, icon: Crown },
    { label: "Devs", value: users.filter((u) => u.role === "dev").length, icon: ShieldCheck },
    { label: "Banned", value: users.filter((u) => u.banned).length, icon: Ban },
    { label: "Events 24h", value: auditStats?.last24h ?? "—", icon: Activity },
  ];

  return (
    <section data-testid="admin-team-section" className="flex flex-col gap-4">
      {/* Stats strip */}
      <div data-testid="team-stats" className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="border border-white/10 bg-black/40 px-4 py-3">
              <p className="font-mono text-[8px] tracking-[0.25em] text-zinc-500 uppercase flex items-center gap-1.5"><Icon className="w-3 h-3 text-nc-blue" /> {s.label}</p>
              <p data-testid={`team-stat-${s.label.toLowerCase().replace(/\s/g, "-")}`} className="font-sans font-bold text-2xl text-zinc-50 mt-1">{s.value}</p>
            </div>
          );
        })}
      </div>

      <div className="border border-white/10 bg-black/40">
        <div className="border-b border-white/10 px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
          <h2 className="font-sans font-semibold uppercase tracking-widest text-base text-zinc-100 flex items-center gap-2">
            <Users className="w-4 h-4 text-nc-blue" /> Team & Accounts
          </h2>
          <div className="flex gap-2">
            {[["users", `Accounts (${users.length})`], ["teams", "Dev Teams"], ["audit", `Audit Log (${audit.length})`], ["removals", `Removals (${removals.length})`]].map(([t, label]) => (
              <button
                key={t}
                data-testid={`admin-team-tab-${t}`}
                onClick={() => setTab(t)}
                className={`font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-2 border transition-colors duration-300 ${
                  tab === t ? "text-nc-blue border-nc-blue/60 bg-nc-blue/10" : "text-zinc-400 border-white/10 hover:border-white/30"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6 flex flex-col gap-3">
          {tab === "users" && (
            <>
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-zinc-600 absolute left-3 top-1/2 -translate-y-1/2" />
                <input data-testid="admin-users-search" className="w-full bg-[#050505] border border-white/10 pl-9 pr-3 py-2.5 font-mono text-sm text-zinc-100 focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue" placeholder="Search by name or email…" value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <p className="font-mono text-[10px] text-zinc-600 leading-relaxed flex items-center gap-2">
                <Eye className="w-3.5 h-3.5 text-nc-orange" /> Click any account to open its personnel file — edit, clearances, bans, deletion and full activity.
              </p>
              {filtered.map((u, i) => (
                <button
                  key={u.id}
                  data-testid={`admin-user-${i + 1}`}
                  onClick={() => setSelected(u)}
                  className={`text-left border p-4 transition-colors duration-200 ${u.banned ? "border-red-500/40 bg-red-500/5 hover:border-red-500/70" : "border-white/10 bg-[#050505]/50 hover:border-nc-blue/50"}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {u.avatar_path ? (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/profile/avatar/${u.id}`} alt="" className="w-9 h-9 rounded-full object-cover border border-white/15 shrink-0" />
                    ) : (
                      <span className="w-9 h-9 rounded-full border border-white/15 bg-nc-blue/10 flex items-center justify-center shrink-0 font-sans font-bold text-xs text-nc-blue">
                        {(u.name || u.email).slice(0, 1).toUpperCase()}
                      </span>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-sans font-semibold text-sm text-zinc-100 truncate">{u.name || "—"}</p>
                        <span className={`font-mono text-[8px] tracking-[0.2em] uppercase border px-1.5 py-0.5 ${roleBadge(u.role)}`}>{u.role}</span>
                        {u.banned && <span data-testid={`admin-user-banned-${i + 1}`} className="font-mono text-[8px] tracking-[0.2em] uppercase border border-red-500/50 bg-red-500/10 text-red-400 px-1.5 py-0.5">banned</span>}
                        {u.totp_enabled && <span className="font-mono text-[8px] tracking-[0.2em] uppercase border border-nc-green/40 bg-nc-green/10 text-nc-green px-1.5 py-0.5">2FA</span>}
                        {me?.id === u.id && <span className="font-mono text-[8px] tracking-[0.2em] uppercase text-zinc-600">you</span>}
                      </div>
                      <p className="font-mono text-[11px] text-zinc-500 truncate">{u.email}</p>
                    </div>
                    <span className="font-mono text-[9px] tracking-[0.2em] text-zinc-600 uppercase shrink-0">Open file →</span>
                  </div>
                </button>
              ))}
              {filtered.length === 0 && <p className="font-mono text-[10px] tracking-[0.25em] text-zinc-600 uppercase">No accounts match.</p>}
            </>
          )}

          {tab === "teams" && <AdminDevTeams users={users} onChanged={load} />}

          {tab === "audit" && (
            <>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="font-mono text-[10px] text-zinc-500 leading-relaxed flex items-center gap-2">
                  <ScrollText className="w-3.5 h-3.5 text-nc-orange" />
                  Every sensitive action in the portal is recorded here — logins, failures, bans, edits, deletions. Use it for investigations.
                </p>
                <input data-testid="audit-filter-input" className="bg-[#050505] border border-white/10 px-3 py-2 font-mono text-xs text-zinc-100 w-56 focus:outline-none focus:ring-1 focus:ring-nc-blue" placeholder="Filter action or actor…" value={auditFilter} onChange={(e) => setAuditFilter(e.target.value)} />
              </div>
              {auditStats && auditStats.byAction.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {auditStats.byAction.slice(0, 6).map((a) => (
                    <span key={a.action} className="font-mono text-[9px] tracking-[0.15em] uppercase border border-white/10 bg-white/5 text-zinc-400 px-2 py-1">
                      {(ACTION_META[a.action] || { label: a.action }).label}: <span className="text-nc-blue">{a.count}</span>
                    </span>
                  ))}
                </div>
              )}
              <div data-testid="audit-list" className="border border-white/10 bg-[#050505]/60 px-4 py-2 max-h-[480px] overflow-y-auto">
                {filteredAudit.length === 0 && <p data-testid="audit-empty" className="font-mono text-[10px] tracking-[0.25em] text-zinc-600 uppercase py-4">No events recorded yet.</p>}
                {filteredAudit.map((e, i) => <AuditRow key={e.id} e={e} i={i} prefix="audit" />)}
              </div>
            </>
          )}

          {tab === "removals" && (
            <>
              {removals.length === 0 && (
                <p data-testid="admin-removals-empty" className="font-mono text-[10px] tracking-[0.25em] text-zinc-600 uppercase">No pending removal requests.</p>
              )}
              {removals.map((d, i) => (
                <div key={d.id} data-testid={`admin-removal-${i + 1}`} className="border border-nc-orange/30 bg-nc-orange/5 p-4">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <p className="font-sans font-bold text-sm text-zinc-100">{d.title}</p>
                      <p className="font-mono text-[10px] text-zinc-500 mt-1">
                        {d.category} // v{d.version} // requested by {d.removal_requested_by} // {String(d.removal_requested_at).slice(0, 10)}
                      </p>
                      <p className="font-mono text-xs text-nc-orange mt-2 leading-relaxed">"{d.removal_reason}"</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button data-testid={`admin-removal-reject-${i + 1}`} onClick={() => rejectRemoval(d.id)} className="font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-2 border border-white/15 text-zinc-300 hover:border-white/40 transition-colors flex items-center gap-1.5">
                        <Undo2 className="w-3.5 h-3.5" /> Keep Doc
                      </button>
                      <button data-testid={`admin-removal-approve-${i + 1}`} onClick={() => approveRemoval(d.id)} className="font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-2 bg-red-500 hover:bg-red-600 text-white transition-colors flex items-center gap-1.5">
                        <Trash2 className="w-3.5 h-3.5" /> Delete Forever
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      <AnimatePresence>
        {selected && (
          <AccountDrawer
            account={selected}
            me={me}
            onClose={() => setSelected(null)}
            onAction={() => { load(); setSelected(null); }}
          />
        )}
      </AnimatePresence>
    </section>
  );
}
